/**
 * The minimum age for a date of birth on a profile (COL-629).
 *
 * Tenants sign a rental contract, so nobody under 18 may be entered — not by
 * the tenant at the signing gate, not by an admin, not from the Brain card.
 * The same rule is enforced three times and must give the same answer each
 * time:
 *
 *   - here, so every form can refuse before it saves;
 *   - supabase/functions/_shared/adult-age.ts, the copy create-contract uses
 *     (edge functions cannot import from src; adult-age.parity.test.ts keeps
 *     the two in step — change both or neither);
 *   - the profiles trigger `enforce_profile_dob_min_age`
 *     (20261102160000_profiles_dob_min_age.sql), which is the backstop.
 *
 * Age is counted on the German calendar date, as the trigger counts it: a
 * person is 18 from the start of their 18th birthday in Europe/Berlin. Someone
 * born on 29 February turns 18 on 1 March in a non-leap year (BGB §187(2),
 * §188(2)), which is what subtracting 18 years from today and clamping
 * 29 February to the 28th gives — the same clamp Postgres applies to
 * `date - interval '18 years'`.
 */

export const MIN_PROFILE_AGE_YEARS = 18;

const pad = (n: number) => String(n).padStart(2, "0");

/** Today's calendar date in Europe/Berlin, as `YYYY-MM-DD`. */
export function berlinToday(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * The latest date of birth that is at least 18 on `today` (`YYYY-MM-DD`).
 * Also usable as the `max` of a date input.
 */
export function latestAdultBirthDate(now: Date = new Date()): string {
  const [y, m, d] = berlinToday(now).split("-").map(Number);
  const year = y - MIN_PROFILE_AGE_YEARS;
  const lastDay = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return `${year}-${pad(m)}-${pad(Math.min(d, lastDay))}`;
}

/** The calendar date a stored or typed date of birth names, or null. */
function calendarDate(raw: string): string | null {
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/**
 * True when `dob` is a date that makes the person younger than 18 today.
 * Empty or unparseable input is not "under age" — callers that require a
 * date check for that separately.
 */
export function isUnderMinimumAge(
  dob: string | null | undefined,
  now: Date = new Date()
): boolean {
  const raw = String(dob ?? "").trim();
  if (!raw) return false;
  const date = calendarDate(raw);
  if (!date) return false;
  return date > latestAdultBirthDate(now);
}
